import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ManagePackage from '../ManagePackage/ManagePackage';
import { logout, getWhatIsMyBusiness } from '../../../Redux/Actions/UserAction';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

/* [CF 2026-08-09] A one-line explanation under each hub row, the way a
   settings screen does it — the labels alone ("Banner", "Manage Services") did
   not say what you would find inside, and the hub was a short list floating on
   an empty page. Keyed by the route's slug so it works for every category's
   own routes without threading new props through twelve pages. Anything not
   listed simply renders without a description. */
const SECTION_DESCRIPTIONS = {
  business: 'Your company name, logo, phone numbers, location and social links.',
  // Towing's profile route is /sp-car-towing — no "business" in it.
  'car-towing': 'Your company name, logo, phone numbers, location and social links.',
  services: 'The services you offer and the car brands you cover.',
  parts: 'The spare parts you stock and the brands they fit.',
  cars: 'The cars listed on your profile.',
  banner: 'Photos shown at the top of your public profile.',
  'working-hour': 'Opening and closing times for each day of the week.',
  package: 'Your current plan, its validity and renewal.',
  'settings-package': 'Your current plan, its validity and renewal.',
  'settings-edit-profile': 'Your name, phone number and password.',
  'settings-saved-items': 'Listings you saved to look at later.',
  'settings-my-posts': 'Cars you have posted for sale.',
  'settings-recently-viewed-cars': 'Cars you opened recently.',
  'settings-become-a-seller': 'Register your company and start listing.',
};

const describeSection = (link) => {
  const slug = (link || '').replace(/^\//, '').split('?')[0];
  if (SECTION_DESCRIPTIONS[slug]) return SECTION_DESCRIPTIONS[slug];
  const key = Object.keys(SECTION_DESCRIPTIONS).find((k) => slug.includes(k));
  return key ? SECTION_DESCRIPTIONS[key] : null;
};

/* [CF 2026-08-17 G4] `extra` lets a page append its own row to the section list
   — Taxi and Towing each carry a "Visible / Hidden" switch that used to live in
   a hand-rolled sidebar. Those two pages built their own markup instead of
   using this component, which is why they alone skipped the mobile hub and
   dropped the owner straight onto the form. They now render the real Sidebar,
   and this prop is where the switch goes. */
const Sidebar = ({ options, showPackage, extra }) => {
  /* [CF 2026-08-17 #11] Every string below used to be hardcoded English, so the
     hub descriptions, the section names and the two lock messages stayed in
     English on the Arabic site. SECTION_DESCRIPTIONS stays keyed by the English
     sentence — that IS the translation key — and is translated at render time,
     because the map is built at module load where `t` does not exist yet. */
  const { t } = useTranslation();
  // Strip any query string — the hub links carry `?open=1` (see below) and
  // without this the active check would compare "sp-garages-business?open=1".
  const currentLocation = (window.location.href.split('/')?.[4] || '').split('?')[0];

  const dispatch = useDispatch();
  const home = useSelector((s) => s.home);

  // Refresh the live business/subscription state so the locks reflect reality
  // (not a stale persisted flag).
  useEffect(() => {
    dispatch(getWhatIsMyBusiness());
  }, [dispatch]);

  // [CF] On mobile the sidebar is a horizontal scroller. Bring the ACTIVE tab
  // into view (centered) so the user always sees which section they're on,
  // instead of it being off-screen to the right.
  const activeRef = useRef(null);
  useEffect(() => {
    const id = setTimeout(() => {
      if (activeRef.current && activeRef.current.scrollIntoView) {
        activeRef.current.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }, 150);
    return () => clearTimeout(id);
  }, [currentLocation]);

  // Owner flow: a business must be created first, then a subscription bought,
  // before the other sections (cars / banner / working hours) unlock.
  const myBusiness = home?.getMybusiness;
  const bid = myBusiness?.businessId || sessionStorage.getItem('BusinessId');
  const businessCreated =
    !!bid && bid !== 'undefined' && bid !== 'null' && bid !== '';
  // [CF] Gate ONLY brand-new (never-subscribed) businesses. EXPIRED owners keep
  // FULL management access — they're only hidden from public listings, not
  // locked out of managing their cars/services/banner/hours.
  //   - hasSubscribed (from /business/my): true once a plan has EVER been bought,
  //     even if it has since expired → expired owners stay unlocked.
  //   - daysLeft > 0: also unlocks demo-renewed businesses that have a future
  //     expiry but no subscription row.
  // Don't trust a persisted 'paymentPlan' flag — it could be stale across tabs.
  const canManage = !!myBusiness?.hasSubscribed || myBusiness?.daysLeft > 0;

  const logOutHandler = (e) => {
    e.preventDefault();
    dispatch(logout());
  };

  /* [CF 2026-08-09] Mobile section navigation: HUB -> page -> back.
     The first option is the section's landing route — "Manage Business" for an
     owner, "Edit Profile" for the personal settings sidebar. That route now
     does double duty: with no flag it shows the HUB (just the list of
     sections, nothing else), and with `?open=1` it shows that section's actual
     content, like any other inner page. That is what lets "Manage Business"
     itself be an inner page — the client asked for the hub to be the list
     alone, and there is no separate hub route to give it.
     Desktop is unaffected: it keeps the full sidebar column and never hides
     the page content. */
  const entryOption = (options || []).find((o) => !o?.isLogout) || null;
  const onEntryRoute =
    !entryOption || currentLocation === entryOption.link?.split('/')?.[1];
  const isNarrow =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 991px)').matches;
  const opened = /[?&]open=1(&|$)/.test(window.location.hash);
  /* [CF 2026-08-17 #4] `hub=1` FORCES the hub. Whether the hub appeared used to
     depend on `onEntryRoute` — the current route matching the first sidebar
     option — which is not the same for every category. So after saving a new
     business some owners landed on the hub and others on the form they had just
     submitted, which is exactly the inconsistency the client reported. The
     post-save redirect now carries this flag so all twelve behave identically. */
  const forcedHub = /[?&]hub=1(&|$)/.test(window.location.hash);
  const showHub = isNarrow && !opened && (onEntryRoute || forcedHub);

  /* While the hub is showing, the page's own content column must be out of the
     way — the hub is a menu, not a form. A body class lets custom.css hide the
     sibling column without this component needing to know what the page
     renders. */
  useEffect(() => {
    document.body.classList.toggle('cav-hub', showHub);
    return () => document.body.classList.remove('cav-hub');
  }, [showHub]);

  const showLock = (reason) => (e) => {
    e.preventDefault();
    Swal.fire({ title: t(reason.title), text: t(reason.text), icon: 'info' });
  };

  return (
    <div className="col-12 col-sm-12 col-md-3 col-lg-3 mb-4 mb-sm-4 mb-md-0 mb-lg-0">
      {/* [CF 2026-08-09] On a phone the plan card belongs to the HUB. Once you
          open a section you are on that page, and repeating the card above
          every form just pushed the content down. Desktop keeps it — there it
          sits in the sidebar column beside the content, not on top of it. */}
      {(showPackage || showPackage === undefined) &&
        businessCreated &&
        (!isNarrow || showHub) && (
        <div className="side-package">
          <ManagePackage />
        </div>
      )}
      {/* [CF 2026-08-09] Client: on the phone, drop the horizontal slider and
          "make it setting one below other, and it goes to inner page on the
          click each time, with back button to come back."
          Agreed — a horizontal scroller hides most of its items off-screen,
          which is what made this feel hard to navigate. So on mobile the
          sections become a stacked list on the ENTRY screen, and every other
          section shows a back row instead. Desktop keeps the sidebar column,
          where the list is fully visible already and a back button would just
          be a detour. `cav-sections` drives the mobile-only CSS; the markup is
          otherwise identical, so nothing about the routing changes. */}
      <div
        className={`side-links-wrap cav-sections ${
          showHub ? 'cav-sections--hub' : 'cav-sections--inner'
        }`}
      >
        {!showHub && isNarrow && entryOption && (
          <Link to={entryOption.link} className="cav-section-back">
            <i className="fas fa-chevron-left cav-section-back-ico" />
            <span>{entryOption.label}</span>
          </Link>
        )}
        <ul>
          {options?.map((opt, index) => {
            // "Manage Business" (the only link containing "business") and the
            // logout action are always available. Everything else is gated —
            // first behind business creation, then behind an ACTIVE subscription.
            // [CF] Active-plan gate restored per client decision: adding/managing
            // cars, banners, services and working hours requires a current
            // subscription (this also blocks expired plans until renewed).
            /* [CF 2026-08-17 G4] The entry row is the one that must always be
               reachable — it is where the business gets created. Matching on
               the word "business" alone missed Towing, whose route is
               `/sp-car-towing`, so once it started using this component its own
               "Manage Profile" row would have been padlocked shut. */
            const isBusinessLink =
              (opt?.link || '').includes('business') ||
              (!!entryOption && opt?.link === entryOption.link);
            // opt.noGate marks personal account pages (Settings sidebar) that
            // every logged-in user can open without creating a business.
            const isGated = !opt?.isLogout && !isBusinessLink && !opt?.noGate;
            const isActive =
              currentLocation === opt?.link?.split('/')?.[1];

            let locked = false;
            let reason = null;
            if (isGated && !businessCreated) {
              locked = true;
              reason = {
                title: 'Create your business first',
                text: 'Please complete "Manage Business" before opening this section.',
              };
            } else if (isGated && !canManage) {
              locked = true;
              reason = {
                title: 'Subscription required',
                text: 'Please purchase a subscription to manage this section.',
              };
            }

            return (
              <li key={index} ref={isActive ? activeRef : null}>
                {locked ? (
                  <a
                    href="#"
                    onClick={showLock(reason)}
                    className="sidebar-locked"
                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    {opt?.icon && <img src={opt?.icon} alt={opt?.label} />}
                    {opt?.label}
                    <span style={{ marginInlineStart: '6px' }}>🔒</span>
                  </a>
                ) : (
                  <Link
                    /* The entry route doubles as the hub, so its own row has to
                       carry `?open=1` — otherwise tapping "Manage Business"
                       from the hub would just re-render the hub. */
                    to={
                      entryOption && opt.link === entryOption.link
                        ? `${opt.link}?open=1`
                        : opt?.link
                    }
                    className={`${isActive ? 'active' : ''}`}
                    onClick={opt?.isLogout ? logOutHandler : null}
                  >
                    {opt?.icon && <img src={opt?.icon} alt={opt?.label} />}
                    <span className="cav-section-text">
                      <span className="cav-section-label">{opt?.label}</span>
                      {showHub && describeSection(opt?.link) && (
                        <span className="cav-section-desc">
                          {t(describeSection(opt?.link))}
                        </span>
                      )}
                    </span>
                  </Link>
                )}
              </li>
            );
          })}
          {extra && <li>{extra}</li>}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
